import classNames from 'classnames';
import * as _ from 'lodash';
import { useCallback } from 'react';
import Helmet from 'react-helmet';
import { Link } from 'react-router';
import type { FunctionComponent, MouseEvent, MouseEventHandler, ReactNode } from 'react';
import type { InjectedRouter } from 'react-router';

import {
  ARCHIVE_AND_LICENSE_UPLOAD,
  BINARY_DECOMPOSITION,
  VCS_SERVICE_IMPORT,
} from '../../../../shared/featureFlags/flagNames';
import { window } from '../../../globals';
import { isFeatureEnabled, useIsFeatureEnabled } from '../../../helpers/features/featureFlags';
import { useGetCurrentPlan } from '../../../hooks/api/subscriptions/useGetCurrentPlan';
import useDispatchOnMount from '../../../hooks/useDispatchOnMount';
import NavigationStore from '../../../redux/navigation';
import { capitalizeSnakeCaseWord, verify_subscription } from '../../../utils';
import PaidFeatureGate from '../../generic/Gates/PaidFeatureGate';
import Icon, { icons, IconText } from '../../generic/Icon';
import DocsLink from '../../generic/Link/DocsLink';
import ReadOnlySection from '../../generic/ReadOnlySection';
import Subnav from '../../generic/Subnav';
import Text from '../../generic/Text';
import Tooltip from '../../generic/Tooltip';
import CreateProjectPage from '../CreateProjectPage';
import CLIUploadGuide from '../upload/CLIUploadGuide';
import type { NavigationState } from '../../../redux/navigation/types';
import type { RouterProps } from '../../../types';
import type { IconClassName } from '../../generic/Icon/icons';

import BulkImportPageCard from './BulkImportPageCard';
import { VcsImportPage } from './github-app/VcsImportPage';
import { AzureLogo } from './icons';
import ServiceImport from './ServiceImport';

import cardStyles from './BulkImportPageCard.module.less';

type BulkImportPageProps = RouterProps & {
  router: InjectedRouter;
};

const AutoBuildHeader = 'Quick Import';
const AutoBuildDesc =
  'Automatically analyze from code host for easy initial results.';
const ProvidedBuildHeader = 'Integrate Locally (CLI)';
const ProvidedBuildDesc =
  'Use your personal or build machine for accurate, secure & performant results.';
const SBOMBuildHeader = 'Import SBOM';
const SBOMBuildDesc = 'Analyze and search your SBOM (CycloneDX and SPDX).';
const BinaryDecompositionBuildHeader = 'Decompose Binary';
const BinaryDecompositionBuildDesc = 'Upload a binary file to be decomposed and analyzed.';

const BulkImportPage: FunctionComponent<BulkImportPageProps> = (props) => {
  const isBinaryDecompositionEnabled = useIsFeatureEnabled(BINARY_DECOMPOSITION);
  const isVcsImportEnabled = useIsFeatureEnabled(VCS_SERVICE_IMPORT);
  const { data: currentPlan, isLoading: isLoadingUsage } = useGetCurrentPlan();

  useDispatchOnMount<[Partial<NavigationState>]>(
    NavigationStore.actions.setNavState,
    {
      expand: 'expanded-sm',
      subnav: null,
      navProps: {},
    },
  );

  const onClickPremiumUploadMethod = useCallback((e: MouseEvent) => {
    e.stopPropagation();
    window.showPlanSelectorModal();
  }, []);

  const renderLastImportOptions = () => {
    const isPremium = verify_subscription('premium');
    const shouldShowOtherOrPublic = window.on_prem || window.user?.super;

    return (
      <>
        <div className="import-link">
          <ImportLink
            title="Azure Repos"
            color="#0078D4"
            absoluteHref="/projects/import/azure_repos"
            svgIcon={<AzureLogo />}
          />
        </div>
        {isFeatureEnabled(ARCHIVE_AND_LICENSE_UPLOAD) &&
          <div className="import-link">
            {isPremium ? (
              <ImportLink
                title="Upload Archive"
                color="#999"
                absoluteHref="/projects/import/upload"
                icon={icons.upload}
              />
            ) : (
              <ImportLink
                title="Upload Archive"
                color="#999"
                absoluteHref="/projects/import/cloud"
                onClick={onClickPremiumUploadMethod}
                icon={icons.upload}
              />
            )}
          </div>}
        {shouldShowOtherOrPublic && (
          <div className="import-link">
            <ImportLink
              title="Other / Public"
              color="#999"
              href="manual"
              icon={icons.globe}
            />
          </div>
        )}
      </>
    );
  };

  const { service } = props.params;

  const isAtOrOverBinaryLimit = !currentPlan?.usage?.binary ||
    currentPlan.usage.binary.usage >= currentPlan.usage.binary.quantity;

  let binaryDecompTooltip: string | ReactNode = 'Loading your usage...';
  // If they don't have a plan or they've hit their quota, show that they're out of binary decompositions.
  if (!isLoadingUsage && isAtOrOverBinaryLimit) {
    binaryDecompTooltip =
      (
        <>
          <div>You are out of binary decompositions.</div>
          <div>Contact us to increase this amount.</div>
        </>
      );
  }

  // Choose import option
  if (service == null) {
    // Only allow premium organizations to access this, but allow the temporary free tier to access it as well.

    return (
      <div className="bg-bubblestream">
        <Helmet title="Add Projects" titleTemplate="%s | FOSSA" />
        <div className="container margin-top text-center">
          <Text.H1>
            <b>Add Projects</b>
          </Text.H1>
          <div className={classNames(cardStyles.cards, 'animated', 'fadeInDown', 'quick')}>
            <BulkImportPageCard
              title={ProvidedBuildHeader}
              isRecommended
              linkTo="/projects/import/cli"
              featureIcons={[icons.upload]}
              description={ProvidedBuildDesc}
              buttonText="View Guide"
              subtitle="No Auth / Code Access"
              subtitleIcon={icons.eyeSlash}
            />
            <BulkImportPageCard
              title={AutoBuildHeader}
              linkTo="/projects/import/cloud"
              featureIcons={[icons.github, icons.bitbucket, icons.gitlab]}
              description={AutoBuildDesc}
              buttonText="Continue"
              subtitle="Authentication Required"
              subtitleIcon={icons.lock}
            />
            <BulkImportPageCard
              title={SBOMBuildHeader}
              linkTo="/projects/import/upload/sbom"
              featureIcons={[icons.cyclone, icons.spdx]}
              description={SBOMBuildDesc}
              buttonText="Upload"
            />
            {isBinaryDecompositionEnabled && (
              <Tooltip
                id="binary-decomp-soft-gate-tooltip"
                showTooltip={isLoadingUsage || isAtOrOverBinaryLimit}
                tooltip={binaryDecompTooltip}
              >
                {/* The span is necessary for the tooltip to wrap the section. */}
                <span>
                  <ReadOnlySection isReadOnly={isLoadingUsage || isAtOrOverBinaryLimit}>
                    <BulkImportPageCard
                      title={BinaryDecompositionBuildHeader}
                      linkTo="/projects/import/upload/binary"
                      featureIcons={[icons.fileBinary]}
                      description={BinaryDecompositionBuildDesc}
                      buttonText="Upload"
                    />
                  </ReadOnlySection>
                </span>
              </Tooltip>
            )}
          </div>
          <p className="padded padded-small text-center">
            Troubleshoot by viewing{' '}
            <DocsLink to="importing-a-project">
              <u>our docs</u>
            </DocsLink>
            .
          </p>
        </div>
      </div>
    );
  }

  // Manual Import
  if (service === 'manual' && (window.on_prem || window.user?.super)) {
    return (
      <div>
        <Helmet title="Manual Import" titleTemplate="%s | FOSSA" />
        <CreateProjectPage {...props} />
      </div>
    );
  }

  // Cloud Import (index)
  if (service === 'cloud') {
    return (
      <div className="container margin-top">
        <Helmet title="Choose Code Host" titleTemplate="%s | FOSSA" />
        <div className="col-md-6 col-md-offset-3">
          <div className="text-center">
            <h2>
              <b>{AutoBuildHeader}</b>
            </h2>
            <p>{AutoBuildDesc}</p>
            <Link className="btn btn-link" to="/projects/import">
              <IconText icon={icons.arrowLeft} text="Go Back" />
            </Link>
          </div>
          <br />
          <div className="animated fadeInUp quick">
            <div className="import-links-row">
              <div className="import-link">
                <ImportLink
                  title="GitHub App"
                  href="github-app"
                  color="#3a3a3a"
                  icon={icons.github}
                />
              </div>
              <div className="import-link">
                <ImportLink
                  title="GitHub OAuth"
                  href="github"
                  color="#3a3a3a"
                  icon={icons.github}
                />
              </div>
              <div className="import-link">
                <ImportLink
                  title="Bitbucket.org"
                  href="bitbucket_cloud"
                  color="#305178"
                  icon={icons.bitbucket}
                />
              </div>
              <div className="import-link">
                <ImportLink
                  title="GitLab"
                  color="#d4361a"
                  href="gitlab"
                  icon={icons.gitlab}
                />
              </div>
              <div className="import-link">
                <PaidFeatureGate
                  isRestricted={!window.on_prem}
                  modalFocusFeature="onPremDeploymentFeature"
                  planToShow="enterprise-compliance"
                >
                  <ImportLink
                    title="Bitbucket Server"
                    color="#305178"
                    href="bitbucket_server"
                    icon={icons.bitbucket}
                  />
                </PaidFeatureGate>
              </div>
              {renderLastImportOptions()}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // CLI Upload Instructions
  if (service === 'cli') {
    return (
      <div className="container margin-top">
        <Helmet title="Install FOSSA CLI" titleTemplate="%s | FOSSA" />
        <div className="col-md-6 col-md-offset-3">
          <div className="text-center">
            <Text.H1>
              <b>{ProvidedBuildHeader}</b>
            </Text.H1>
            <p
              className="text-center"
              style={{ maxWidth: '24rem', margin: '0 auto 16px' }}
            >
              {ProvidedBuildDesc}
            </p>
            <Link className="btn btn-link" to="/projects/import">
              <IconText icon={icons.arrowLeft} text="Go Back" />
            </Link>
          </div>
          <br />
          <div className="animated fadeInUp quick">
            <CLIUploadGuide />
          </div>
        </div>
      </div>
    );
  }

  // The vcs-service import is a page of its own: it owns its header and layout, so it returns
  // before the legacy import shell's Subnav and container.
  if (service === 'github-app' && isVcsImportEnabled) {
    return <VcsImportPage />;
  }

  // Proper service import
  // TODO: validate import method
  return (
    <div>
      <Helmet title={`Import From ${capitalizeSnakeCaseWord(service)}`} titleTemplate="%s | FOSSA" />
      <Subnav>
        <Subnav.Item to="/projects/import/cloud">
          <IconText icon={icons.arrowLeft} text="Back to Services" />
        </Subnav.Item>
      </Subnav>
      <div className="container margin-top">
        <ServiceImport location={props.location} service={service} />
      </div>
    </div>
  );
};

const ImportLink: FunctionComponent<{
  color: string;
  icon?: IconClassName;
  title: string;
  href?: string;
  absoluteHref?: string;
  onClick?: MouseEventHandler;
  svgIcon?: ReactNode;
}> = ({
  color,
  icon,
  title,
  href,
  absoluteHref,
  onClick = _.noop,
  svgIcon,
}) => {
  return (
    <Link
      onClick={onClick}
      to={absoluteHref || `/projects/import/${href || title.toLowerCase()}`}
      activeClassName="active"
      className="card text-center"
      style={{
        position: 'relative',
        borderTop: `solid 3px ${color}`,
        color,
      }}
    >
      {icon ? (
        <span className="import-link-icon margin-bottom-xs">
          <Icon icon={icon} />
        </span>
      ) : (
        svgIcon
      )}
      <h4 style={{ color }}>{title}</h4>
    </Link>
  );
};

export default BulkImportPage;
